import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useStore } from '../lib/store'

const SPECIALTIES = [
  // Primary Care
  'Family Medicine',
  'Internal Medicine',
  'Pediatrics',
  'Geriatrics',
  // Surgical Specialties
  'General Surgery',
  'Orthopedic Surgery',
  'Spine Surgery',
  'Hand Surgery',
  'Neurosurgery',
  'Cardiothoracic Surgery',
  'Vascular Surgery',
  'Plastic & Reconstructive Surgery',
  'Urology',
  'Colorectal Surgery',
  'Pediatric Surgery',
  'Transplant Surgery',
  'Trauma Surgery',
  'Surgical Oncology',
  'Oral & Maxillofacial Surgery',
  'Bariatric Surgery',
  // Medical Specialties
  'Cardiology',
  'Pulmonology / Critical Care',
  'Gastroenterology',
  'Endocrinology',
  'Nephrology',
  'Rheumatology',
  'Neurology',
  'Hematology',
  'Hematology / Oncology',
  'Medical Oncology',
  'Radiation Oncology',
  'Infectious Disease',
  'Allergy & Immunology',
  'Dermatology',
  'Physical Medicine & Rehab',
  'Pain Management',
  'Sports Medicine',
  'Sleep Medicine',
  'Palliative Care / Hospice',
  // Surgical Subspecialties
  'ENT / Otolaryngology',
  'Ophthalmology',
  'OB/GYN',
  'Maternal-Fetal Medicine',
  'Reproductive Endocrinology',
  'Gynecologic Oncology',
  'Podiatry',
  // Hospital-Based
  'Emergency Medicine',
  'Hospital Medicine / Hospitalist',
  'Critical Care / ICU',
  'Anesthesiology',
  'Radiology',
  'Interventional Radiology',
  'Pathology',
  'Nuclear Medicine',
  // Psychiatry & Behavioral
  'Psychiatry',
  'Child & Adolescent Psychiatry',
  'Addiction Medicine',
  'Psychology',
  // Dental / Allied
  'Dentistry',
  'Oral Surgery',
  'Periodontics',
  'Endodontics',
  'Orthodontics',
  'Prosthodontics',
  // Other
  'Chiropractic',
  'Optometry',
  'Occupational Medicine',
  'Urgent Care',
  'Wound Care',
  'Physical Therapy',
  'Neonatology',
  'Genetics / Genomics',
  'Other',
]

const EMR_SYSTEMS = [
  // Major Systems
  'Epic',
  'Cerner / Oracle Health',
  'MEDITECH',
  // Ambulatory / Practice
  'athenahealth',
  'eClinicalWorks',
  'NextGen Healthcare',
  'Allscripts / Veradigm',
  'ModMed (Modernizing Medicine)',
  'DrChrono',
  'Practice Fusion',
  'AdvancedMD',
  'Greenway Health',
  'Kareo / Tebra',
  'CureMD',
  'Amazing Charts',
  'ChartLogic',
  'Elation Health',
  'RXNT',
  'Praxis EMR',
  'IMS (Meditab)',
  'Office Ally',
  // Specialty-Specific
  'EMA by Modernizing Medicine',
  'Nextech',
  'Compulink (Ophthalmology)',
  'NexTech (Dermatology)',
  'WRS Health',
  // Dental
  'Dentrix',
  'Eaglesoft',
  'Open Dental',
  // VA / Government
  'VistA / CPRS',
  // Other
  'Other EHR',
  'Paper Charts',
  'None',
]

type PracticeSize = 'solo' | '2-5' | '6-20' | '20+'

export function RegisterPage() {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [specialty, setSpecialty] = useState('')
  const [practiceSize, setPracticeSize] = useState<PracticeSize | ''>('')
  const [currentEmr, setCurrentEmr] = useState('')
  
  // Legal agreements
  const [agreeTerms, setAgreeTerms] = useState(false)
  const [agreePrivacy, setAgreePrivacy] = useState(false)
  const [agreeBaa, setAgreeBaa] = useState(false)
  
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const { register } = useStore()
  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    // Validation
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }
    if (!specialty) {
      setError('Please select your specialty')
      return
    }
    if (!practiceSize) {
      setError('Please select your practice size')
      return
    }
    if (!currentEmr) {
      setError('Please select your current EMR')
      return
    }
    if (!agreeTerms || !agreePrivacy || !agreeBaa) {
      setError('You must agree to all terms, privacy policy, and BAA')
      return
    }

    setLoading(true)
    try {
      await register({ 
        email, 
        password, 
        name, 
        specialty,
        practice_size: practiceSize,
        current_emr: currentEmr,
        agree_terms: agreeTerms,
        agree_privacy: agreePrivacy,
        agree_baa: agreeBaa,
      })
      navigate('/')
    } catch (err: any) {
      setError(err.message || 'Registration failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-brand-500">Dr. Scribey</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-2">Create your account</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-gray-100 dark:bg-gray-900 rounded-xl p-6 space-y-4 shadow-xl border border-gray-200 dark:border-gray-800">
          {error && (
            <div className="bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-800 text-red-600 dark:text-red-300 text-sm rounded-lg p-3">
              {error}
            </div>
          )}

          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Full Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500"
                placeholder="Dr. Jane Smith"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500"
                placeholder="you@example.com"
                required
              />
            </div>
          </div>

          {/* Specialty */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Specialty <span className="text-red-500 dark:text-red-400">*</span>
            </label>
            <select
              value={specialty}
              onChange={(e) => setSpecialty(e.target.value)}
              className="w-full bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500"
              required
            >
              <option value="">Select your specialty</option>
              {SPECIALTIES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          {/* Practice Size */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Practice Size <span className="text-red-500 dark:text-red-400">*</span>
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { value: 'solo', label: 'Solo' },
                { value: '2-5', label: '2-5 providers' },
                { value: '6-20', label: '6-20 providers' },
                { value: '20+', label: '20+ providers' },
              ].map((option) => (
                <label
                  key={option.value}
                  className={`flex items-center justify-center px-4 py-3 rounded-lg border-2 cursor-pointer transition-colors ${
                    practiceSize === option.value
                      ? 'border-brand-500 bg-brand-100 dark:bg-brand-500/10 text-brand-400'
                      : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <input
                    type="radio"
                    name="practiceSize"
                    value={option.value}
                    checked={practiceSize === option.value}
                    onChange={(e) => setPracticeSize(e.target.value as PracticeSize)}
                    className="sr-only"
                    required
                  />
                  <span className="text-sm font-medium">{option.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Current EMR */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Current EMR <span className="text-red-500 dark:text-red-400">*</span>
            </label>
            <select
              value={currentEmr}
              onChange={(e) => setCurrentEmr(e.target.value)}
              className="w-full bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500"
              required
            >
              <option value="">Select your EMR system</option>
              {EMR_SYSTEMS.map((emr) => (
                <option key={emr} value={emr}>
                  {emr}
                </option>
              ))}
            </select>
          </div>

          {/* Password */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500"
                placeholder="Min. 8 characters"
                required
                minLength={8}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Confirm Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500"
                placeholder="••••••••"
                required
              />
            </div>
          </div>

          {/* Legal Agreements */}
          <div className="border-t border-gray-200 dark:border-gray-800 pt-4 space-y-3">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Legal Agreements</p>
            
            <label className="flex items-start gap-3 cursor-pointer group">
              <div className="relative flex-shrink-0 mt-0.5">
                <input
                  type="checkbox"
                  checked={agreeTerms}
                  onChange={(e) => setAgreeTerms(e.target.checked)}
                  className="w-5 h-5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-brand-500 checked:bg-brand-600 checked:border-brand-600"
                  required
                />
              </div>
              <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-800 dark:group-hover:text-gray-200">
                I agree to the{' '}
                <a href="/terms" target="_blank" className="text-brand-400 hover:text-brand-300 underline">
                  Terms of Service
                </a>
                <span className="text-red-500 dark:text-red-400 ml-1">*</span>
              </span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer group">
              <div className="relative flex-shrink-0 mt-0.5">
                <input
                  type="checkbox"
                  checked={agreePrivacy}
                  onChange={(e) => setAgreePrivacy(e.target.checked)}
                  className="w-5 h-5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-brand-500 checked:bg-brand-600 checked:border-brand-600"
                  required
                />
              </div>
              <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-800 dark:group-hover:text-gray-200">
                I agree to the{' '}
                <a href="/privacy" target="_blank" className="text-brand-400 hover:text-brand-300 underline">
                  Privacy Policy
                </a>
                <span className="text-red-500 dark:text-red-400 ml-1">*</span>
              </span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer group">
              <div className="relative flex-shrink-0 mt-0.5">
                <input
                  type="checkbox"
                  checked={agreeBaa}
                  onChange={(e) => setAgreeBaa(e.target.checked)}
                  className="w-5 h-5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-brand-500 checked:bg-brand-600 checked:border-brand-600"
                  required
                />
              </div>
              <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-800 dark:group-hover:text-gray-200">
                I agree to the{' '}
                <a href="/baa" target="_blank" className="text-brand-400 hover:text-brand-300 underline">
                  Business Associate Agreement (BAA)
                </a>
                <span className="text-red-500 dark:text-red-400 ml-1">*</span>
              </span>
            </label>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-brand-600 hover:bg-brand-500 disabled:bg-brand-800 text-white font-medium py-3 rounded-lg transition-colors mt-6"
          >
            {loading ? 'Creating account...' : 'Create Account'}
          </button>

          <p className="text-center text-sm text-gray-500 dark:text-gray-400">
            Already have an account?{' '}
            <Link to="/login" className="text-brand-400 hover:text-brand-300">
              Sign in
            </Link>
          </p>
        </form>
      </div>
    </div>
  )
}
